// Curseurs live (façon Miro) — publication DIRECTE navigateur → hub Mercure.
//
// Contrairement aux données métier (publiées par le backend via API Platform),
// les positions de curseur partent directement du navigateur vers le hub :
// à ~10 positions/s par utilisateur, transiter par PHP écroulerait le backend,
// alors que le hub Go absorbe ce volume sans effort.
//
// Le jeton de publication est demandé à /api/mercure/cursor-token : il est
// limité au sélecteur `spot/cursors/{+path}` — impossible de publier sur les
// topics métier avec.

import { useEffect, useRef } from 'react';
import { MERCURE_HUB_URL } from './mercure';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Palette des collaborateurs : chaque utilisateur garde LA MÊME couleur pour
 * son curseur live ET son avatar « sur le tableau » (en-tête de page).
 */
export const CURSOR_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#8b5cf6', '#ef4444', '#84cc16'];
export const cursorColorFor = (id) => CURSOR_COLORS[Math.abs(Number(id) || 0) % CURSOR_COLORS.length];

let tokenCache = null;   // { token, exp } — partagé entre toutes les pages
let tokenPromise = null;

async function getPublishToken() {
    const now = Math.floor(Date.now() / 1000);
    if (tokenCache && tokenCache.exp - 60 > now) return tokenCache.token;
    if (!tokenPromise) {
        tokenPromise = fetch(`${API_URL}/mercure/cursor-token`, { credentials: 'include' })
            .then((r) => r.json())
            .then((d) => {
                tokenCache = d;
                tokenPromise = null;
                return d.token;
            })
            .catch((e) => {
                tokenPromise = null;
                throw e;
            });
    }
    return tokenPromise;
}

/** Publie un événement curseur sur le hub. Jamais bloquant : les curseurs sont du confort. */
export async function publishCursor(topic, data, { keepalive = false } = {}) {
    const attempt = async () => {
        const token = await getPublishToken();
        const body = new URLSearchParams();
        body.set('topic', topic);
        body.set('data', JSON.stringify(data));
        const res = await fetch(MERCURE_HUB_URL, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body,
            keepalive,
        });
        // Jeton expiré / hub qui refuse : on invalide le cache pour le prochain essai
        if (res.status === 401 || res.status === 403) {
            tokenCache = null;
            throw new Error('cursor publish unauthorized');
        }
        return res;
    };
    try {
        await attempt();
    } catch {
        try {
            tokenCache = null;
            await attempt();
        } catch {
            // silencieux : un curseur perdu n'est pas une erreur
        }
    }
}

/** S'abonne à un topic curseur brut (les topics métier passent par useMercure). */
export function useCursorTopic(topic, onMessage, enabled = true) {
    const cbRef = useRef(onMessage);
    cbRef.current = onMessage;

    useEffect(() => {
        if (!enabled || !topic || typeof EventSource === 'undefined') return undefined;
        let es = null;
        let retryTimer = null;
        let stopped = false;

        const connect = () => {
            const url = new URL(MERCURE_HUB_URL);
            url.searchParams.append('topic', topic);
            es = new EventSource(url.toString());
            es.onmessage = (e) => {
                try { cbRef.current?.(JSON.parse(e.data)); } catch { /* payload invalide : ignoré */ }
            };
            es.onerror = () => {
                if (stopped) return;
                if (es.readyState === EventSource.CLOSED) {
                    // Reconnexion rapide : les curseurs sont du temps réel
                    retryTimer = setTimeout(connect, 1500);
                }
            };
        };

        connect();

        return () => {
            stopped = true;
            if (retryTimer) clearTimeout(retryTimer);
            if (es) es.close();
        };
    }, [topic, enabled]);
}
